import React, { useEffect, useRef, useState } from "react";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { Button, Checkbox, Input, Popconfirm, Tag, message } from "antd";
import NutritionDrawer, { DrawerFooter } from "@components/nutrition/nutrition-drawer";
import {
  assignDocExpClientCard,
  fetchDocExpClientCard,
  lookupDocExpClientCard,
  revokeDocExpClientCard
} from "./doc-exp-api";
import { DOC_STATUS_LABELS } from "@core/card-keys";

const lookupLine = (res) => {
  if (!res) return null;
  if (res.state === "free") {
    return { ok: true, text: "Карта свободна" };
  }
  if (res.state === "busy" && res.holder) {
    const st = DOC_STATUS_LABELS[String(res.holder.idStatus)] || "";
    return { ok: false, text: `Занята: Путёвка №${res.holder.idDoc} (${st})${res.holder.name ? `, ${res.holder.name}` : ""}` };
  }
  return { ok: false, text: res.message || "Карта недоступна" };
};

const DocExpClientCardDrawer = ({ open, idDoc, idStr, clientName, onClose, onSaved }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const inputRef = useRef(null);
  const [card, setCard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [key, setKey] = useState("");
  const [lookup, setLookup] = useState(null);
  const [move, setMove] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoaded(false);
    try {
      const data = await fetchDocExpClientCard(idDoc, idStr);
      setCard(data && data.idCard ? data : null);
    } catch (e) {
      messageApi.error(e?.message || "Не удалось загрузить карту");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!open || !idDoc || !idStr) return;
    setKey("");
    setLookup(null);
    setMove(false);
    load();
  }, [open, idDoc, idStr]);

  const doLookup = async () => {
    const k = key.trim();
    if (!k) return;
    try {
      const res = await lookupDocExpClientCard(idDoc, idStr, k);
      setLookup(res);
      setMove(false);
    } catch (e) {
      setLookup({ state: "notfound", message: e?.message || "Ошибка поиска" });
    }
  };

  const assign = async () => {
    setSaving(true);
    try {
      const res = await assignDocExpClientCard(idDoc, idStr, { key: key.trim(), move });
      setCard(res);
      setLookup(null);
      setKey("");
      messageApi.success("Карта выдана");
      onSaved?.();
    } catch (e) {
      messageApi.error(e?.message || "Не удалось выдать карту");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    setSaving(true);
    try {
      await revokeDocExpClientCard(idDoc, idStr);
      setCard(null);
      setLookup(null);
      messageApi.success("Карта снята");
      onSaved?.();
    } catch (e) {
      messageApi.error(e?.message || "Не удалось снять карту");
    } finally {
      setSaving(false);
    }
  };

  const line = lookupLine(lookup);
  const canAssign = lookup && (lookup.state === "free" || (lookup.state === "busy" && lookup.canMove && move));

  return (
    <NutritionDrawer
      open={open}
      title={`Карта гостя${clientName ? ` — ${clientName}` : ""}`}
      width={440}
      onClose={onClose}
    >
      {contextHolder}
      {loaded && card ? (
        <div className="doc-exp-card__guest-card">
          <div className="doc-exp-card__guest-card-row"><span>Код</span><code>{card.keyCard}</code></div>
          <div className="doc-exp-card__guest-card-row"><span>Номер</span><b>{card.keyCardShort || "—"}</b></div>
          <div className="doc-exp-card__guest-card-row"><span>Тип</span>{card.vidName || "—"}</div>
          {Number(card.isDel) === 1 ? <Tag color="red">Заблокирована</Tag> : <Tag color="green">Выдана</Tag>}
          <Popconfirm title="Снять карту с гостя?" onConfirm={revoke}>
            <Button danger loading={saving} style={{ marginTop: 12 }}>Снять карту</Button>
          </Popconfirm>
        </div>
      ) : loaded ? (
        <div className="doc-exp-card__guest-card">
          <Input
            ref={inputRef}
            autoFocus
            size="large"
            autoComplete="off"
            placeholder="Поднесите карту к считывателю или введите код"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onPressEnter={doLookup}
          />
          {line ? (
            <div className={`doc-exp-card__guest-card-status${line.ok ? " ok" : " err"}`}>
              {line.ok ? <CheckCircleFilled /> : <ExclamationCircleFilled />}
              <span>{line.text}</span>
            </div>
          ) : null}
          {lookup?.state === "busy" && lookup.canMove ? (
            <Checkbox checked={move} onChange={(e) => setMove(e.target.checked)}>
              Перенести карту с выписанной путёвки
            </Checkbox>
          ) : null}
          {lookup?.state === "busy" && !lookup.canMove && lookup.message ? (
            <div className="doc-exp-card__guest-card-status err">
              <ExclamationCircleFilled />
              <span>{lookup.message}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      <DrawerFooter
        onSave={canAssign ? assign : null}
        onClose={onClose}
        saving={saving}
        saveText="Выдать карту"
      />
    </NutritionDrawer>
  );
};

export default DocExpClientCardDrawer;
